/**
 * The world-space box a collider entity occupies, for the box-based collision checks (walk, fly,
 * orbit, picking).
 *
 * A collider built from the scene data has its box precomputed when it loads (`_collisionBounds`:
 * a primitive's transformed unit box, or an uploaded model's bounds). An editor collider has none,
 * because the gizmo keeps moving it, so its box comes from its CURRENT world transform with the same
 * formula, and it collides exactly where, and like, the same collider will once saved. (Reading only
 * position and local scale ignored rotation: a vertical plane wall became a horizontal slab.)
 */
import * as pc from 'playcanvas';

export interface ColliderBox {
    cx: number; cy: number; cz: number;
    hx: number; hy: number; hz: number;
}

interface BoxedCollider extends pc.GraphNode {
    _collisionBounds?: pc.BoundingBox;
}

/** Writes `entity`'s world box into `out` and returns it. */
export function colliderBox(entity: pc.GraphNode, out: ColliderBox): ColliderBox {
    const bounds = (entity as BoxedCollider)._collisionBounds;
    if (bounds) {
        out.cx = bounds.center.x; out.cy = bounds.center.y; out.cz = bounds.center.z;
        out.hx = bounds.halfExtents.x; out.hy = bounds.halfExtents.y; out.hz = bounds.halfExtents.z;
        return out;
    }
    // A primitive spans [-0.5, 0.5] locally; each column of the 3×3 part is a scaled basis vector,
    // so a world half-extent is half the sum of that row's absolute values.
    const m = entity.getWorldTransform().data;
    out.cx = m[12]; out.cy = m[13]; out.cz = m[14];
    out.hx = (Math.abs(m[0]) + Math.abs(m[4]) + Math.abs(m[8])) * 0.5;
    out.hy = (Math.abs(m[1]) + Math.abs(m[5]) + Math.abs(m[9])) * 0.5;
    out.hz = (Math.abs(m[2]) + Math.abs(m[6]) + Math.abs(m[10])) * 0.5;
    return out;
}
